/* eslint-disable @typescript-eslint/restrict-plus-operands */
import moment from 'moment';
import { get, camelCase } from 'lodash-es';

const isoStringFormat = 'YYYY-MM-DD HH:mm:ss ZZ';

function getTimeFromMinutes(mins: number, format = 'hh:mm A') {
  if (!mins && mins !== 0) { return ''; }

  // tslint:disable no-bitwise
  const h = mins / 60 | 0;
  const m = mins % 60 | 0;
  return moment.utc().hours(h).minutes(m).format(format);
}

function getDelayTimeFromMinutes(mins: number) {
  if (!mins) {
    return '';
  }

  if (mins < 60) {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    return String(mins + ' mins');
  }

  // tslint:disable-next-line: no-bitwise
  const h = mins / 60 | 0; const m = mins % 60 | 0;
  return moment.utc().hours(h).minutes(m).format('hh:mm') + ' hour';
}

function getReadableDate(date: moment.MomentInput) {
  const onlyDate = moment().format('YYYY-MM-DD');
  const todayDate = moment(onlyDate, 'YYYY-MM-DD');
  const mydate = moment(date, isoStringFormat);
  const diffInDays = mydate.diff(todayDate, 'days');
  let availableDate = mydate.format('ddd, DD MMM');
  // tslint:disable-next-line: triple-equals
  if (diffInDays == 0) {
    availableDate = 'Today, ' + mydate.format('DD MMM');
  }
  // tslint:disable-next-line: triple-equals
  if (diffInDays == 1) {
    availableDate = 'Tomorrow, ' + mydate.format('DD MMM');
  }

  return availableDate;

}

function getAgeFromDOB(date: moment.MomentInput, withoutFormat = false) {
  if (!date) return withoutFormat ? {} : '';
  // tslint:disable-next-line: one-variable-per-declaration
  const dob = moment(date, isoStringFormat),
    now = moment(),
    y = now.diff(dob, 'year'),
    m = now.diff(dob.add(y, 'years'), 'months'),
    d = now.diff(dob.add(m, 'months'), 'days');

  if (withoutFormat) { return ({ y, m, d }); }
  return [y && `${y}y`, m && `${m}m`, d && `${d}d`].filter(Boolean).join(' ');
}

function getDelayedBy(delay: number) {
  const hrs = Math.floor(delay / 60);
  const mins = delay % 60;
  if (hrs && mins) {
    // tslint:disable triple-equals
    return `${hrs} hr${hrs == 1 ? '' : 's'} ${mins} min${mins == 1 ? '' : 's'
      }`;
  } else if (hrs) {
    return `${hrs} hr${hrs == 1 ? '' : 's'}`;
  } else {
    return `${mins} min${mins == 1 ? '' : 's'}`;
  }
}

function getUtcClock() {
  return moment().hours() * 60 * 60 + moment().minutes() * 60 + moment().seconds();
}

function getNextPathname({ type }: any = {}) {
  let pathname = '/queue';
  switch (type) {
    case 'queue':
      pathname = '/queue';
      break;
    case 'appointments':
      pathname = '/appointment';
      break;
  }

  return pathname;
}

function getAddressString(adr, msg = false) {
  const {
    line1,
    line2,
    locality,
    area,
    pincode,
    city,
    state,
  } = adr || {};
  const address: string[] = [];
  if (line1) { address.push(line1); }
  if (line2) { address.push(line2); }
  if (locality) { address.push(locality); }
  if (area) { address.push(area); }
  if (pincode && !msg) { address.push(pincode); }
  if (city && !msg) { address.push(city); }
  if (state && !msg) { address.push(state); }

  return address.join(', ');
}

function formatMobileNumber(num: string | number) {
  return '+91' + `${num}`.slice(-10);
}

function isTodayDate(date: any, format: string = isoStringFormat) {
  return moment(date, format).isSame(moment(), 'day');
}

function hexToRgbA(hex: string, transparency = 1) {
  let c: any;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length == 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + transparency + ')';
  }
  throw new Error('Bad Hex');
}

function formatAmount(amount: number) {
  return amount ? parseFloat(amount as any).toLocaleString('en-IN') : amount;
}

function formatDate({
  date,
  format = 'DD/MM/YYYY',
  fallback = 'N/A',
  dateFormat = isoStringFormat,
}: any) {
  if (date)
    return moment(date, dateFormat).format(format);

  return fallback;
}

function postDateFormat(date: any) {
  return moment.utc(date, isoStringFormat).format(isoStringFormat);
}

interface IDAmount {
  amount: number;
  withIcon?: boolean;
  defaultValue?: string;
}

function displayAmount({
  amount,
  withIcon = true,
  defaultValue = '-',
}: IDAmount) {
  if (amount === 0 || amount) {
    return withIcon ? `₹${formatAmount(amount)}` : amount;
  }
  return defaultValue;
}

function customDateFormat(value, format = 'ddd, D MMM YYYY') {
  const isToday = isTodayDate(value);
  return `${isToday ? 'Today, ' : ''}${moment(value).format(isToday ? 'D MMM YYYY' : format)}`;
}

function isPastDate(appointmentDate: any, withTime?: boolean) {
  return moment(appointmentDate).isBefore(withTime ? moment() : moment('00:00', 'HH:mm'));
}

function isMobileNumber(str: string, len?: number) {
  const isValid = /^((\+91)?0?[6-9][0-9]{5,9})$/.test(str);

  if (isValid && len) {
    return `${str}`.length === len;
  }

  return isValid;
}

function isEmail(str: string) {
  return /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(str);
}

function isName(str: string) {
  if (/^[a-z\s\\(\\)\\/.]+$/i.test(str)) {
    return true;
  }

  return false;
}

function isDateOfBirth(str: any) {
  const date = moment(str, ['DD/MM', 'DD-MM-YYYY', 'DD-MM', 'DD/MM/YYYY'], true);
  if (date.isValid() && str.length >= 4) {
    // && !/^\d+$/.test(str)
    return true;
  }

  return false;
}

function padNumber(val: string | number) {
  if (val < 10) { return `0${val}`; }

  return val;
}

const pageMap = {
  '/login': 'login',
  '/queue': 'patient_queue',
  '/appointment': 'appointment_scheduler',
};

const patientProfileRegex = /.*?patient\/(.w?)/g;

const BASENAME = process.env.REACT_APP_BASENAME || '/';
function getPageName(pathname: string = location.pathname) {
  pathname = pathname.replace(BASENAME, '');
  const pageName = pageMap[pathname];
  if (pageName) { return pageName; }

  if (pathname.match(patientProfileRegex)) {
    return 'patient_dashboard';
  }
  return pathname;
}

function getEventSource() {
  // const selectedTab = '';
  return (tab?: string) => {
    if (tab) {
      // selectedTab = tab;
    } else {
      const pageName = getPageName();
      switch (pageName) {
        // case 'patient_queue':
        //   return EVENT_QUEUE_TABS[selectedTab];
        // case 'patient_dashboard':
        //   return PATIENT_EVENT_TAB[selectedTab];
        default:
          return pageName;
      }
    }
  };
}

function getKeysNKeyMap(array = [], key = 'id', useCamelCase = false) {
  const data = [];
  const keys: string[] = [];
  const keyMap = {};
  array.forEach(item => {
    const value = get(item, key);
    const keyValue: string = useCamelCase ? camelCase(value) : value;

    if (!keyMap[keyValue]) {
      keys.push(keyValue);
      data.push(item);
      keyMap[keyValue] = item;
    }
  });

  return { data, keys, keyMap };
}

const pluralize = (count, noun, suffix = 's') => `${count} ${noun}${count != 1 ? suffix : ''}`;

const getNSetEventSource = getEventSource();
export {
  getTimeFromMinutes,
  getDelayTimeFromMinutes,
  getNextPathname,
  getReadableDate,
  getAgeFromDOB,
  getDelayedBy,
  getUtcClock,
  getAddressString,
  formatMobileNumber,
  isTodayDate,
  hexToRgbA,
  formatAmount,
  formatDate,
  postDateFormat,
  displayAmount,
  customDateFormat,
  isPastDate,
  isName,
  isMobileNumber,
  isEmail,
  isDateOfBirth,
  padNumber,
  getPageName,
  getNSetEventSource,
  getKeysNKeyMap,
  pluralize,
};
